import React from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter } from "react-router-dom";

// MUI font
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import App from "./App";
import Auth0ProviderWithRedirectCallback from "./components/auth/Auth0ProviderWithRedirectCallback";
import CookieConsentBanner from "./components/auth/CookieConsent";
import GoogleAnalyticsHeader from "./components/auth/google_analytics_header";
import GoogleTagManagerHeader from "./components/auth/google_tag_manager_header";
import * as config from "./configs/config";
import { AUTH0_SESSION_CHECK_EXPIRY_DAYS } from "./constants/auth0SessionConfig";
import SocketProvider from "./context/SocketContextProvider";
import reportWebVitals from "./reportWebVitals";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { initializeAppInsights, logMetric } from "./telemetry/appInsights";

// Initialize Application Insights
initializeAppInsights();

const root = createRoot(document.getElementById("root") as HTMLElement);

root.render(
  <React.StrictMode>
    <HelmetProvider>
      <GoogleAnalyticsHeader />
      <GoogleTagManagerHeader />
      <CookieConsentBanner />
      <BrowserRouter>
        <Auth0ProviderWithRedirectCallback
          props={{
            domain: config.REACT_APP_AUTH0_DOMAIN,
            clientId: config.REACT_APP_AUTH0_CLIENT_ID,
            authorizationParams: {
              redirect_uri: config.REACT_APP_AUTH0_REDIRECT_URI,
              audience: config.REACT_APP_AUTH0_AUDIENCE,
              scope: "openid profile email offline_access",
            },
            cacheLocation: "localstorage",
            // The SDK will not even look at the token it already holds
            // once this cookie lapses, so the one-day default signed
            // people out while their session sat unread beside it.
            sessionCheckExpiryDays: AUTH0_SESSION_CHECK_EXPIRY_DAYS,
          }}
        >
          <SocketProvider>
            <App />
          </SocketProvider>
        </Auth0ProviderWithRedirectCallback>
      </BrowserRouter>
    </HelmetProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals((metric) => {
  logMetric(`WebVital_${metric.name}`, metric.value, {
    id: metric.id,
    name: metric.name,
    value: metric.value,
    delta: metric.delta,
  });
});

// Register service worker for offline caching and better update handling.
// The service worker caches static assets, reducing chunk load errors during deployments.
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    console.log("New version available. Service worker updated.", registration);
  },
  onSuccess: (registration) => {
    console.log("Content cached for offline use.", registration);
  },
});
